import {
  useEffect, useRef, useState, useCallback,
} from 'react';
import SC from './style';

interface HubspotFormProps {
  region?: string;
  portalId: string;
  formId: string;
  onFormSubmitted?: (form: any, data: any) => void;
  prefillData?: Record<string, string>;
}

const HubspotForm: React.FC<HubspotFormProps> = ({
  region = 'na1', portalId, formId, onFormSubmitted, prefillData,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const formContainerRef = useRef<HTMLDivElement>(null);
  const scriptLoadedRef = useRef(false);

  const createForm = useCallback(() => {
    if (window.hbspt && formContainerRef.current) {
      const formConfig: any = {
        region,
        portalId,
        formId,
        target: `#${formContainerRef.current.id}`,
        cssClass: 'formStyle',
        onFormSubmitted: (form: any, data: any) => {
          if (onFormSubmitted) {
            onFormSubmitted(form, data);
          }
        },
      };

      // Ajouter les données de pré-remplissage si elles existent
      if (prefillData) {
        formConfig.onFormReady = ($form: any) => {
          Object.entries(prefillData).forEach(([fieldName, value]) => {
            const field = $form.find(`[name="${fieldName}"]`);
            if (field.length > 0) {
              field.val(value).trigger('change');
            }
          });
        };
      }

      window.hbspt.forms.create(formConfig);
    }
  }, [region, portalId, formId, onFormSubmitted, prefillData]);

  useEffect(() => {
    // Charger le script HubSpot seulement une fois
    if (scriptLoadedRef.current) {
      return undefined;
    }

    const script = document.createElement('script');
    script.src = '//js.hsforms.net/forms/embed/v2.js';
    script.type = 'text/javascript';
    script.async = true;

    script.onload = () => {
      scriptLoadedRef.current = true;
    };

    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    // Créer le formulaire quand la modal s'ouvre
    if (isModalOpen) {
      createForm();
    }
  }, [isModalOpen, createForm]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const introText = `Vous avez une question ou un besoin spécifique ?

Si vous avez une attente particulière, une question sur le programme, ou si vous rencontrez une contrainte technique ou une difficulté pouvant impacter votre parcours de formation, notre équipe est à votre écoute.

Nous vous invitons à nous contacter afin d'échanger avec vous et de vous proposer la solution la plus adaptée à votre situation et à vos besoins.`;

  return (
    <>
      <SC.DivContainer>
        <SC.DivIntroContainer>
          <SC.PIntroText>{introText}</SC.PIntroText>
          <SC.ButtonOpenForm onClick={openModal}>
            Nous contacter
          </SC.ButtonOpenForm>
        </SC.DivIntroContainer>
      </SC.DivContainer>

      <SC.DivModalOverlay isOpen={isModalOpen} onClick={handleOverlayClick}>
        <SC.DivModalContent>
          <SC.ButtonCloseModal onClick={closeModal} aria-label="Fermer">
            ×
          </SC.ButtonCloseModal>
          <SC.DivFormContainer id={`hubspot-form-${formId}`} ref={formContainerRef} />
        </SC.DivModalContent>
      </SC.DivModalOverlay>
    </>
  );
};

export default HubspotForm;
